import { useMemo } from "react";
import { Field } from "@design-system/Field/Field";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EntityLabel } from "./EntityLabel";
import { OrganizationIntegrationWorkspaceOption } from "./OrganizationIntegrationsContext";
import styles from "./AssignmentFields.module.scss";

interface ClientFieldProps {
    workspaces: OrganizationIntegrationWorkspaceOption[];
    workspaceId: string | null;
    onChange: (workspaceId: string | null) => void;
}

export const ClientField = ({
    workspaces,
    workspaceId,
    onChange,
}: ClientFieldProps) => {
    const items = useMemo(
        () =>
            workspaces.map((workspace) => ({
                value: workspace.id,
                label: workspace.name ?? "Untitled client",
            })),
        [workspaces],
    );

    return (
        <Field label="Client" control="button" className={styles.field}>
            <Picker.Select
                searchable
                searchPlaceholder="Search clients..."
                emptyLabel="No clients found"
                items={items}
                value={workspaceId}
                onValueChange={onChange}
                placeholder="Select client"
                trigger={<Picker.Button variant="primary" size="md" block />}
            />
        </Field>
    );
};

interface EntityFieldProps {
    workspace: OrganizationIntegrationWorkspaceOption | undefined;
    entityId: number | null;
    onChange: (entityId: number | null) => void;
}

export const EntityField = ({
    workspace,
    entityId,
    onChange,
}: EntityFieldProps) => {
    const items = useMemo(
        () =>
            workspace === undefined
                ? []
                : workspace.entities.map((entity) => ({
                      value: entity.id,
                      label: entity.name ?? "Untitled entity",
                      leading: (
                          <EntityAvatar size="xs" entity={entity} aria-hidden />
                      ),
                  })),
        [workspace],
    );

    const selectedEntity = workspace?.entities.find(
        (entity) => entity.id === entityId,
    );

    return (
        <Field label="Entity" control="button" className={styles.field}>
            <Picker.Select
                searchable
                searchPlaceholder="Search entities..."
                emptyLabel="No entities found"
                items={items}
                value={selectedEntity === undefined ? null : entityId}
                onValueChange={onChange}
                disabled={workspace === undefined}
                placeholder="Select entity"
                renderValue={() =>
                    selectedEntity ? (
                        <EntityLabel entity={selectedEntity} />
                    ) : undefined
                }
                trigger={<Picker.Button variant="primary" size="md" block />}
            />
        </Field>
    );
};
